import { PlusCircle } from "@tamagui/lucide-icons";
import { useCreateCustomer, CreateCustomerInput } from "common";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Button, Input, Label, Spinner, XStack, YStack, Text } from "tamagui";

import { removeEmptyStrings } from "../../utils";

interface CustomerFormProps {
  onCreateNew: (customer: CreateCustomerInput) => Promise<void>;
  loading?: boolean;
}
const CustomerForm: React.FC<CustomerFormProps> = ({
  onCreateNew,
  loading = false,
}) => {
  const [newCustomer, setNewCustomer] = useState<CreateCustomerInput>({
    id: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "USA",
    companyName: "",
  });

  const handleCreateNew = async () => {
    await onCreateNew(newCustomer);
  };

  return (
    <YStack space="$4" padding="$4">
      <YStack space="$3">
        <XStack space="$3">
          <YStack flex={1} space="$2">
            <Label htmlFor="name">ID</Label>
            <Input
              id="id"
              placeholder="ID"
              value={newCustomer.id}
              onChangeText={(text) =>
                setNewCustomer({ ...newCustomer, id: text })
              }
            />
          </YStack>
          <YStack flex={1} space="$2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Full Name"
              value={newCustomer.name}
              onChangeText={(text) =>
                setNewCustomer({ ...newCustomer, name: text })
              }
            />
          </YStack>
          <YStack flex={1} space="$2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              placeholder="Email Address"
              keyboardType="email-address"
              autoCapitalize="none"
              value={newCustomer.email}
              onChangeText={(text) =>
                setNewCustomer({ ...newCustomer, email: text })
              }
            />
          </YStack>
        </XStack>
        <XStack space="$3">
          <YStack flex={1} space="$2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              placeholder="Phone Number"
              keyboardType="phone-pad"
              value={newCustomer.phone}
              onChangeText={(text) =>
                setNewCustomer({ ...newCustomer, phone: text })
              }
            />
          </YStack>
          <YStack flex={1} space="$2">
            <Label htmlFor="companyName">Company Name (Optional)</Label>
            <Input
              id="companyName"
              placeholder="Company Name"
              value={newCustomer.companyName}
              onChangeText={(text) =>
                setNewCustomer({ ...newCustomer, companyName: text })
              }
            />
          </YStack>
        </XStack>
        <YStack space="$2">
          <Label htmlFor="address">Address</Label>
          <Input
            id="address"
            placeholder="Street Address"
            value={newCustomer.address}
            onChangeText={(text) =>
              setNewCustomer({ ...newCustomer, address: text })
            }
          />
        </YStack>
        <XStack space="$3">
          <YStack flex={1} space="$2">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              placeholder="City"
              value={newCustomer.city}
              onChangeText={(text) =>
                setNewCustomer({ ...newCustomer, city: text })
              }
            />
          </YStack>
          <YStack flex={1} space="$2">
            <Label htmlFor="state">State</Label>
            <Input
              id="state"
              placeholder="State"
              value={newCustomer.state}
              onChangeText={(text) =>
                setNewCustomer({ ...newCustomer, state: text })
              }
            />
          </YStack>
          <YStack flex={1} space="$2">
            <Label htmlFor="zipCode">Zip Code</Label>
            <Input
              id="zipCode"
              placeholder="Zip Code"
              keyboardType="number-pad"
              value={newCustomer.zipCode}
              onChangeText={(text) =>
                setNewCustomer({ ...newCustomer, zipCode: text })
              }
            />
          </YStack>
        </XStack>
        <Button
          onPress={handleCreateNew}
          disabled={loading || !newCustomer.name}
          icon={loading ? undefined : PlusCircle}
          theme="active"
        >
          {loading ? <Spinner /> : "Create Customer"}
        </Button>
      </YStack>
    </YStack>
  );
};

export const CreateCustomer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { create, loading } = useCreateCustomer();

  const onCreateNew = async (data) => {
    const sanitized = removeEmptyStrings<CreateCustomerInput>(data);
    await create(sanitized);
    navigate("/customers");
  };

  return (
    <YStack flex={1} padding="$4" space="$4" backgroundColor="$backgroundSoft">
      <XStack
        justifyContent="space-between"
        alignItems="center"
        marginBottom="$2"
      >
        <Text fontSize="$8" fontWeight="bold" color="$color">
          {t("customerCreate.title", "Create Customer")}
        </Text>
        <Button theme="active" size="$4" onPress={() => navigate("/customers")}>
          Go Back
        </Button>
      </XStack>
      <YStack space="$4" flex={1} paddingTop="$4">
        <CustomerForm onCreateNew={onCreateNew} loading={loading} />
      </YStack>
    </YStack>
  );
};

export default CreateCustomer;
